import { SWR_KEYS } from '@bulknutrients/bulk-shared-lib/constants';
import { isErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import type {
  AddItemGiftCertificate,
  Cart,
  CartLineItem,
} from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import { useCustomer } from '@hooks/customer/useCustomer';
import { log } from '@lib/logs';
import type { AddCartItemAPIResponse } from 'pages/api/cart';
import { useSWRConfig } from 'swr';

export function useAddItems({ include }: { include?: string[] }) {
  const { mutate } = useSWRConfig();
  const { customer } = useCustomer();

  return async ({
    lineItems,
    giftCertificate,
  }: {
    lineItems?: Array<CartLineItem>;
    giftCertificate?: AddItemGiftCertificate;
  }): Promise<{
    data: Cart | null;
    error: string | null;
  }> => {
    const response = await fetch('/api/cart', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        item: {
          lineItems,
          giftCertificate,
        },
        include,
      }),
    });

    try {
      const res: AddCartItemAPIResponse = await response.json();

      // Can we be more useful with error message
      if (isErrorResponse(res)) {
        let errorMessage = res.error.message.join('. ');
        if (errorMessage.includes('sufficient stock')) {
          errorMessage =
            'There is not currently sufficient stock of an item(s)';
        } else if (errorMessage.includes('No variants')) {
          errorMessage = 'Item(s) not currently available';
        }

        return {
          data: null,
          error: errorMessage,
        };
      }

      if (res.data && res.data.cart.customer_id === 0 && customer) {
        // add customer_id to cart
        await fetch('/api/cart/add-customer-id', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            cartId: res.data.cart.id,
            customerId: customer?.entityId,
          }),
        });
      }

      mutate(SWR_KEYS.cart);

      return {
        data: res.data.cart,
        error:
          res.data.unavailableItems.length > 0
            ? `${res.data.unavailableItems.length === 1 ? 'That item is' : 'Some items are'} unavailable currently${res.data.unavailableItems.length === 1 ? '.' : ', but we have added what we can to your cart'}`
            : null,
      };
    } catch (err) {
      log({ error: err, location: 'useAddItem' });
      return {
        data: null,
        error: 'Unexpected remote server error',
      };
    }
  };
}
